import { AlertTriangle } from 'lucide-react';
import { useI18n } from '@/i18n';
import { computePromptBudget, type ModelLike } from '../lib/prompt-budget';

interface PromptBudgetHintProps {
  systemPrompt: string;
  mainModel?: ModelLike | null;
}

/**
 * Soft counter + warning for the system prompt (CHG-AGT-008).
 *
 * Never blocks the editor: the backend enforces the model budget and rejects
 * over-budget prompts with a stable public error. All texts are i18n (ES/EN)
 * and show only the public model name — never internal IDs.
 */
export const PromptBudgetHint = ({ systemPrompt, mainModel }: PromptBudgetHintProps) => {
  const { t } = useI18n();

  if (!systemPrompt) return null;

  const { tokens, budget, model, overBudget } = computePromptBudget(systemPrompt, mainModel);
  const modelLabel = model || t('admin.agents.editor.prompts.unknownModel');

  return (
    <div className="space-y-1" data-testid="agent-prompt-budget">
      <p className="text-xs text-muted-foreground">
        {t('admin.agents.editor.prompts.tokenCounter', { tokens, budget, model: modelLabel })}
      </p>
      {overBudget && (
        <p
          role="status"
          data-testid="agent-prompt-over-budget"
          className="flex items-center gap-1 text-xs text-amber-600"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t('admin.agents.editor.prompts.overBudgetWarning')}
        </p>
      )}
    </div>
  );
};
